import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatNumber } from '@/lib/format';
import { PAGE_SIZES, sortRows, usePage, useSort, type SortState, type SortValue } from '@/lib/hooks';
import { IconButton } from './button';
import { EmptyState, QueryError, SkeletonRows } from './feedback';

/**
 * The one table. Every list in the panel is this component with a column
 * spec, so sorting, paging, selection, alignment and the empty/loading/error
 * states behave the same everywhere:
 *  - text left, numbers right, actions right, a status in its own column;
 *  - a sortable header shows its arrow, the active one in the accent;
 *  - sort and page live in the URL (`sort`, `page`, `size`, prefixed when a
 *    page holds several tables), so a view survives a reload and can be sent;
 *  - the footer says what is shown of how much, and lets the page size change.
 */
export interface Column<T> {
  key: string;
  header: ReactNode;
  align?: 'left' | 'right' | 'center';
  /** A width utility, e.g. `w-10` for a checkbox column. */
  width?: string;
  /** Present = sortable, by this value. */
  sortValue?: (row: T) => SortValue;
  render: (row: T) => ReactNode;
  className?: string;
  /** The cell holds its own controls (links, switches): a click there must not open the row. */
  stopClick?: boolean;
  /** Hide the column below this breakpoint. */
  hideBelow?: 'md' | 'lg' | 'xl';
}

export interface DataTableProps<T> {
  /** URL param prefix when a page holds several tables. */
  id?: string;
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  defaultSort: SortState;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  empty: { icon: ReactNode; title: string; description?: string; action?: ReactNode };
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
  selectable?: { selected: Set<string>; onChange: (next: Set<string>) => void };
  /** Hide the footer for a short list inside a card. */
  compact?: boolean;
  className?: string;
}

const HIDE: Record<NonNullable<Column<unknown>['hideBelow']>, string> = { md: 'hidden md:table-cell', lg: 'hidden lg:table-cell', xl: 'hidden xl:table-cell' };
const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

export function DataTable<T>({ id = '', rows, columns, rowKey, defaultSort, loading, error, onRetry, empty, onRowClick, rowClassName, selectable, compact, className }: DataTableProps<T>) {
  const [sort, toggleSort] = useSort(defaultSort, id);
  const { page, setPage, pages, size, setSize } = usePage(rows.length, id);

  if (loading) return <SkeletonRows cols={Math.min(columns.length, 6)} />;
  if (error) return <QueryError error={error} onRetry={onRetry} />;
  if (rows.length === 0) return <EmptyState {...empty} />;

  const accessors = Object.fromEntries(columns.filter((c) => c.sortValue).map((c) => [c.key, c.sortValue as (r: T) => SortValue]));
  const sorted = sortRows(rows, sort, accessors);
  const pageRows = compact ? sorted : sorted.slice((page - 1) * size, page * size);

  const allOnPage = selectable ? pageRows.length > 0 && pageRows.every((r) => selectable.selected.has(rowKey(r))) : false;
  const toggleAll = () => {
    if (!selectable) return;
    const next = new Set(selectable.selected);
    if (allOnPage) pageRows.forEach((r) => next.delete(rowKey(r))); else pageRows.forEach((r) => next.add(rowKey(r)));
    selectable.onChange(next);
  };
  const toggleOne = (key: string) => {
    if (!selectable) return;
    const next = new Set(selectable.selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    selectable.onChange(next);
  };

  const from = (page - 1) * size + 1;
  const to = Math.min(rows.length, page * size);

  return (
    <div className={cn('animate-fade-in', className)}>
      <div className="scroll-thin overflow-x-auto rounded-2xl border border-line bg-surface shadow-card">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead>
            <tr>
              {selectable && (
                <th className="w-10 border-b border-line bg-surface px-4 py-2.5">
                  <input type="checkbox" aria-label="Выбрать все на странице" checked={allOnPage} onChange={toggleAll} className="accent-[var(--accent)]" />
                </th>
              )}
              {columns.map((c) => {
                const sortable = !!c.sortValue;
                const active = sort.key === c.key;
                const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
                return (
                  <th
                    key={c.key}
                    aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                    className={cn('border-b border-line bg-surface text-[11px] font-semibold uppercase tracking-wider text-muted', sortable ? 'p-0' : 'px-4 py-2.5', ALIGN[c.align ?? 'left'], c.width, c.hideBelow && HIDE[c.hideBelow])}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(c.key)}
                        className={cn('flex h-full w-full items-center gap-1 px-4 py-2.5 uppercase tracking-wider transition-colors hover:text-ink', c.align === 'right' ? 'justify-end text-right' : c.align === 'center' ? 'justify-center' : 'text-left', active && 'text-ink')}
                      >
                        {c.header}
                        <Icon size={12} className={active ? 'shrink-0 text-accent' : 'shrink-0 text-faint opacity-60'} />
                      </button>
                    ) : c.header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => {
              const key = rowKey(row);
              return (
                <tr
                  key={key}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn('transition-colors [&:last-child>td]:border-b-0 hover:bg-surface-2/70', onRowClick && 'cursor-pointer', selectable?.selected.has(key) && 'bg-accent-soft/40', rowClassName?.(row))}
                >
                  {selectable && (
                    <td className="border-b border-line px-4 py-3 align-middle" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" aria-label="Выбрать строку" checked={selectable.selected.has(key)} onChange={() => toggleOne(key)} className="accent-[var(--accent)]" />
                    </td>
                  )}
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      onClick={c.stopClick ? (e) => e.stopPropagation() : undefined}
                      className={cn('border-b border-line px-4 py-3 align-middle', ALIGN[c.align ?? 'left'], c.align === 'right' && 'tabular-nums', c.hideBelow && HIDE[c.hideBelow], c.className)}
                    >
                      {c.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!compact && rows.length > PAGE_SIZES[0] && (
        <div className="flex flex-wrap items-center justify-between gap-3 px-1 pt-3 text-xs text-muted">
          <span>Показано {formatNumber(from)}–{formatNumber(to)} из {formatNumber(rows.length)}</span>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5">
              На странице
              <select value={size} onChange={(e) => setSize(Number(e.target.value))} aria-label="Строк на странице" className="h-7 rounded-md border border-line bg-surface px-1.5 text-xs text-ink">
                {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            {pages > 1 && (
              <div className="flex items-center gap-0.5">
                <IconButton size="sm" title="Первая страница" disabled={page <= 1} onClick={() => setPage(1)}><ChevronsLeft size={14} /></IconButton>
                <IconButton size="sm" title="Предыдущая страница" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft size={14} /></IconButton>
                <span className="min-w-[4.5rem] text-center tabular-nums">{page} / {pages}</span>
                <IconButton size="sm" title="Следующая страница" disabled={page >= pages} onClick={() => setPage(page + 1)}><ChevronRight size={14} /></IconButton>
                <IconButton size="sm" title="Последняя страница" disabled={page >= pages} onClick={() => setPage(pages)}><ChevronsRight size={14} /></IconButton>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
